import React, { useState, useEffect } from "react";
import { X, FolderPlus, Loader2, Folder } from "lucide-react";

const PRESET_COLORS = [
  { label: "Blue", hex: "#3B82F6" },
  { label: "Indigo", hex: "#6366F1" },
  { label: "Purple", hex: "#8B5CF6" },
  { label: "Emerald", hex: "#10B981" },
  { label: "Amber", hex: "#F59E0B" },
  { label: "Rose", hex: "#F43F5E" },
  { label: "Cyan", hex: "#06B6D4" }
];

export default function CreateFolderModal({
  isOpen,
  onClose,
  currentFolder,
  onSubmit,
  loading
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#3B82F6");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setName("");
      setColor("#3B82F6");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please enter a folder name.");
      return;
    }
    setError("");
    try {
      await onSubmit({
        name: name.trim(),
        parentId: currentFolder ? currentFolder.id : null,
        color
      });
      onClose();
    } catch (err) {
      console.error("Error creating folder:", err);
      setError(err.message || "Failed to create folder.");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content glass-card animate-slide-up" style={{ maxWidth: "460px" }}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div 
              style={{ 
                width: "36px", 
                height: "36px", 
                borderRadius: "8px", 
                backgroundColor: `${color}20`, 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                color: color
              }}
            >
              <FolderPlus size={20} />
            </div>
            <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "var(--text-primary)" }}>
              New Folder
            </h3>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ marginTop: "1.25rem" }}>
          {currentFolder && (
            <div 
              style={{ 
                padding: "0.6rem 0.85rem", 
                backgroundColor: "var(--bg-input)", 
                border: "1px solid var(--border-color)", 
                borderRadius: "6px", 
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.85rem",
                color: "var(--text-secondary)"
              }}
            >
              <Folder size={16} style={{ color: currentFolder.color || "var(--primary)" }} />
              <span>Location: <strong>{currentFolder.name}</strong></span>
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="folderName">Folder Name</label>
            <input
              id="folderName"
              type="text"
              required
              autoFocus
              placeholder="e.g. Sales Collateral, Pitch Decks, Case Studies"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="form-group" style={{ marginTop: "1.25rem" }}>
            <label className="form-label">Folder Color Accent</label>
            <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginTop: "0.4rem" }}>
              {PRESET_COLORS.map((c) => {
                const isSelected = color === c.hex;
                return (
                  <button
                    key={c.hex}
                    type="button"
                    title={c.label}
                    onClick={() => setColor(c.hex)}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: c.hex,
                      border: isSelected ? "3px solid var(--text-primary)" : "2px solid transparent",
                      boxShadow: isSelected ? "0 0 8px rgba(0,0,0,0.3)" : "none",
                      cursor: "pointer",
                      transition: "transform 0.15s ease",
                      transform: isSelected ? "scale(1.15)" : "scale(1)"
                    }}
                  />
                );
              })}
            </div>
          </div>

          {error && <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{error}</div>}

          <div className="modal-footer" style={{ marginTop: "1.75rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={16} style={{ marginRight: "0.4rem" }} />
                  Creating...
                </>
              ) : (
                "Create Folder"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
